import React from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";

import Modal from "../../../../../layout/modal";

const SURVEY_TARGET_FLAGS = {
    PROFILE: 65536,
    LEAD: 131072,
    CLIENT: 262144,
    ATTORNEY: 524288,
    JUDGE: 1048576,
    STAFF: 2097152,
    MATTER_DIVORCE: 4194304,
    MATTER_CUSTODY: 8388608,
    MATTER_CHILD_SUPPORT: 16777216,
    MATTER_GENERAL_CIVIL: 33554432,
    MATTER_NAME_CHANGE: 67108864
};

const MATTER_FLAGS = {
    DIVORCE: 1,
    CUSTODY: 2,
    CHILD_SUPPORT: 4,
    SPOUSAL_SUPPORT: 8,
    CIVIL: 16,
    CRIMINAL: 32,
    TRAFFIC: 64,
    PERSONAL_INJURY: 128,
    NAME_CHANGE: 256
};

class ProcessModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.getDefaultState(props);
    }

    getDefaultState = (props = this.props) => {
        const translatedFlags = this.translateSurveyFlags(props.flags);
        const compatible = this.getCompatibleMatters(props.matters, translatedFlags);

        return {
            translatedFlags,
            compatible,
            selectedMode: compatible.length ? "update" : "create",
            selectedMatterUuid: compatible.length ? compatible[0].uuid : null
        };
    };

    componentDidUpdate(prevProps) {
        const flagsChanged = prevProps.flags !== this.props.flags;
        const mattersChanged = prevProps.matters !== this.props.matters;
        const showChanged = prevProps.show !== this.props.show;

        if (flagsChanged || mattersChanged || showChanged) {
            this.setState(this.getDefaultState(this.props));
        }
    }

    hasFlag = (value, flag) => Boolean((Number(value) || 0) & Number(flag));

    translateSurveyFlags = (surveyFlags) => {
        let result = 0;

        if (this.hasFlag(surveyFlags, SURVEY_TARGET_FLAGS.MATTER_DIVORCE)) {
            result |= MATTER_FLAGS.DIVORCE;
        }

        if (this.hasFlag(surveyFlags, SURVEY_TARGET_FLAGS.MATTER_CUSTODY)) {
            result |= MATTER_FLAGS.CUSTODY;
        }

        if (this.hasFlag(surveyFlags, SURVEY_TARGET_FLAGS.MATTER_CHILD_SUPPORT)) {
            result |= MATTER_FLAGS.CHILD_SUPPORT;
        }

        if (this.hasFlag(surveyFlags, SURVEY_TARGET_FLAGS.MATTER_GENERAL_CIVIL)) {
            result |= MATTER_FLAGS.CIVIL;
        }
        
        if (this.hasFlag(surveyFlags, SURVEY_TARGET_FLAGS.MATTER_NAME_CHANGE)) {
            result |= MATTER_FLAGS.NAME_CHANGE;
        }

        return result;
    };

    getCompatibleMatters = (matters = [], translatedFlags = 0) => {
        if (!Array.isArray(matters)) return [];

        return matters.filter((matter) => {
            const matterFlags = Number(matter?.matterFlags || 0);
            return Boolean(matterFlags & translatedFlags);
        });
    };

    getDefaultCaption = () => {
        const person = this.props.person || {};
        const displayName =
            [person.firstName, person.lastName].filter(Boolean).join(" ").trim() || "Matter";

        const translatedFlags = this.state?.translatedFlags ?? this.translateSurveyFlags(this.props.flags);

        if (translatedFlags & MATTER_FLAGS.DIVORCE) return `${displayName} Divorce`;
        if (translatedFlags & MATTER_FLAGS.CUSTODY) return `${displayName} Custody`;
        if (translatedFlags & MATTER_FLAGS.CHILD_SUPPORT) return `${displayName} Child Support`;
        if (translatedFlags & MATTER_FLAGS.CIVIL) return `${displayName} Civil Matter`;
        if (translatedFlags & MATTER_FLAGS.NAME_CHANGE) return `${displayName} Name Change`;

        return displayName;
    };

    onModeChange = (selectedMode) => {
        this.setState({
            selectedMode
        });
    };

    onMatterSelect = (selectedMatterUuid) => {
        this.setState({
            selectedMode: "update",
            selectedMatterUuid
        });
    };

    onOk = (e) => {
        if (e) e.preventDefault();

        const payload = {
            mode: this.state.selectedMode,
            matterUuid: this.state.selectedMode === "update" ? this.state.selectedMatterUuid : null,
            matterFlags: this.state.translatedFlags,
            title: this.props.title,
            surveyFlags: this.props.flags,
            data: this.props.data || {},
            person: this.props.person || {},
            defaultCaption: this.getDefaultCaption()
        };

        if (this.props.onOk) this.props.onOk(payload);
    };

    onCancel = (e) => {
        if (e) e.preventDefault();
        if (this.props.onCancel) this.props.onCancel();
    };

    renderMatterRow = (matter, index) => {
        const selected = this.state.selectedMatterUuid === matter.uuid;

        return (
            <Row className="g-0 sh-6 mb-2" key={`${matter.uuid}_${index}`}>
                <Col>
                    <div className="d-flex flex-row pt-0 pb-0 ps-3 pe-3 h-100 align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                            <div>{matter.caption || "[Untitled Matter]"}</div>
                            <div className="text-small text-muted">
                                {matter?.Court?.full || matter?.["Court.full"] || ""}
                                {matter?.caseNumber ? ` · ${matter.caseNumber}` : ""}
                            </div>
                        </div>
                        <div className="d-flex">
                            <Button
                                size="sm"
                                variant={selected ? "primary" : "outline-primary"}
                                onClick={() => this.onMatterSelect(matter.uuid)}
                            >
                                {selected ? "Selected" : "Use This Matter"}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    render() {
        const compatible = this.state.compatible || [];
        const hasCompatible = compatible.length > 0;

        return (
            <Modal
                show={this.props.show}
                title="Process Survey Results"
                body={`This screen will help you import the results of "${this.props.title}". Choose whether to create a new matter or apply the intake to an existing compatible matter.`}
                buttons={[
                    {
                        caption: "ok",
                        onEnabledClick: this.onOk,
                        default: true,
                        variant: "primary",
                        enabled:
                            this.state.selectedMode === "create" ||
                            Boolean(this.state.selectedMatterUuid),
                        display: true
                    },
                    {
                        caption: "cancel",
                        onEnabledClick: this.onCancel,
                        variant: "outline-primary",
                        enabled: true,
                        display: true
                    }
                ]}
            >
                <Card className="mb-3">
                    <Card.Body>
                        <Form.Check
                            type="radio"
                            id="process-create"
                            name="process-mode"
                            className="mb-2"
                            checked={this.state.selectedMode === "create"}
                            onChange={() => this.onModeChange("create")}
                            label={`Create a new matter${this.state.translatedFlags ? ` (${this.getDefaultCaption()})` : ""}`}
                        />

                        {hasCompatible && (
                            <Form.Check
                                type="radio"
                                id="process-update"
                                name="process-mode"
                                checked={this.state.selectedMode === "update"}
                                onChange={() => this.onModeChange("update")}
                                label="Apply to an existing compatible matter"
                            />
                        )}
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="mb-n2">
                        {this.state.selectedMode === "create" ? (
                            <div className="text-muted">
                                A new matter will be opened and the survey data will be prepared for import.
                            </div>
                        ) : null}

                        {this.state.selectedMode === "update" && !hasCompatible ? (
                            <div className="text-muted">
                                No compatible existing matters were found for this survey type.
                            </div>
                        ) : null}

                        {this.state.selectedMode === "update" && hasCompatible
                            ? compatible.map(this.renderMatterRow)
                            : null}
                    </Card.Body>
                </Card>
            </Modal>
        );
    }
}

export default ProcessModal;
